/**
 * Esquemas de validación (Zod) de las ENTRADAS de cada operación.
 *
 * Se validan en la Cloud Function antes de tocar Firestore, y el frontend
 * reutiliza los mismos esquemas para validar formularios. Una sola definición,
 * cero desincronización.
 */
import { z } from 'zod';
const uid = z.string().min(1, 'UID requerido');
const observacion = z.string().trim().max(1000).optional();
/** Crear expediente. */
export const CrearExpedienteInput = z.object({
    radicado: z.string().trim().min(1, 'El radicado es obligatorio'),
    titulo: z.string().trim().min(1, 'El título es obligatorio'),
    area: z.string().trim().min(1),
    responsableEmail: uid,
    oficinaId: z.string().min(1).nullable().default(null),
    ubicacionFisica: z.string().trim().nullable().default(null),
    talonario: z.string().trim().nullable().default(null),
    nota: z.string().trim().max(1000).nullable().default(null),
});
/** Solicitar un préstamo (crea el préstamo en estado Pendiente). */
export const SolicitarPrestamoInput = z.object({
    expedienteId: z.string().min(1),
    asignadoEmail: uid, // usuario destino que deberá aceptar
    observaciones: observacion,
});
/** Aceptar un préstamo pendiente. */
export const AceptarPrestamoInput = z.object({
    expedienteId: z.string().min(1),
    prestamoId: z.string().min(1),
    observaciones: observacion,
});
/** Endosar un préstamo aceptado a otro usuario. */
export const EndosarPrestamoInput = z.object({
    expedienteId: z.string().min(1),
    prestamoId: z.string().min(1),
    destinatarioEmail: uid,
    observaciones: observacion,
});
/** Devolver (regresar) un préstamo. */
export const DevolverPrestamoInput = z.object({
    expedienteId: z.string().min(1),
    prestamoId: z.string().min(1),
    observaciones: observacion,
});
/** Cambiar la ubicación física de un expediente. */
export const CambiarUbicacionInput = z.object({
    expedienteId: z.string().min(1),
    ubicacionFisica: z.string().trim().min(1),
    observaciones: observacion,
});
/** Cambiar la oficina de uno o varios expedientes. */
export const CambioOficinaInput = z.object({
    expedienteIds: z.array(z.string().min(1)).min(1),
    oficinaId: z.string().min(1),
    observaciones: observacion,
});
// ===== Administración (solo admin) =========================================
/** Editar los datos de un expediente. */
export const EditarExpedienteInput = z.object({
    expedienteId: z.string().min(1),
    titulo: z.string().trim().optional(),
    area: z.string().trim().optional(),
    oficinaId: z.string().min(1).nullable().optional(),
    ubicacionFisica: z.string().trim().nullable().optional(),
    talonario: z.string().trim().nullable().optional(),
    responsableEmail: z.string().trim().optional(),
});
/** Forzar la devolución de un expediente (admin), sin importar quién lo tenga. */
export const ForzarDevolucionInput = z.object({
    expedienteId: z.string().min(1),
    motivo: observacion,
});
/** Habilitar/deshabilitar el acceso de un usuario al módulo y sus accesos. */
export const GestionarAccesoInput = z.object({
    email: z.string().trim().email(),
    activo: z.boolean().optional(),
    accesos: z.array(z.string()).optional(), // claves de MODULOS que puede ver
});
/**
 * Acceso MASIVO a un módulo del sistema.
 *  - todos:        se lo damos a todo el mundo
 *  - ninguno:      se lo quitamos a todo el mundo
 *  - soloEstos:    solo los correos indicados lo ven (nadie más)
 *  - exceptoEstos: todos lo ven MENOS los correos indicados
 */
export const AccesoMasivoInput = z.object({
    modulo: z.string().min(1),
    modo: z.enum(['todos', 'ninguno', 'soloEstos', 'exceptoEstos']),
    emails: z.array(z.string().trim().email()).default([]),
});
/** Fijar la clave de kiosco de un usuario concreto. */
export const ClaveKioscoInput = z.object({
    email: z.string().trim().email(),
    password: z.string().min(6, 'Mínimo 6 caracteres (lo exige Firebase Auth)'),
});
/**
 * Crear un usuario NATIVO del programa (no viene del portal de funcionarios).
 * Entra con alias + contraseña; no requiere cuenta de Google.
 */
export const CrearUsuarioNativoInput = z.object({
    alias: z.string().trim().min(3, 'Mínimo 3 caracteres')
        .regex(/^[a-z0-9._-]+$/i, 'Solo letras, números, punto, guion y guion bajo'),
    nombre: z.string().trim().min(1, 'El nombre es obligatorio'),
    apellido: z.string().trim().default(''),
    password: z.string().min(6, 'Mínimo 6 caracteres (lo exige Firebase Auth)'),
    area: z.string().trim().default(''),
    oficinaId: z.string().trim().default(''),
    accesos: z.array(z.string()).optional(),
});
/** Entregar folios para que los anexen a una carpeta (cji-df:otros). */
export const CrearOtroDocumentoInput = z.object({
    expedienteId: z.string().min(1),
    folios: z.coerce.number().int().min(1, 'Mínimo 1 folio').max(9999),
    descripcion: z.string().trim().min(1, 'Describa el documento').max(300),
    asignadoEmail: z.string().trim().email(),
    formulario: z.string().trim().max(40).optional(),
});
/** Aceptar (anexar) los folios entregados. */
export const AceptarOtroDocumentoInput = z.object({
    expedienteId: z.string().min(1),
    otroId: z.string().min(1),
});
/** Rango de fechas de un reporte. `desde`/`hasta` en ISO. */
export const ReporteTurnoInput = z.object({
    desde: z.string().min(4),
    hasta: z.string().min(4),
    prestadorEmail: z.string().trim().email().optional(),
});
/** Cerrar turno: calcula el reporte del periodo y lo envía a los líderes. */
export const CerrarTurnoInput = z.object({
    desde: z.string().min(4),
    hasta: z.string().min(4),
    notas: z.string().trim().max(1000).default(''),
    destinatarios: z.array(z.string().trim().email()).default([]),
});
/** Crear o editar una oficina. */
export const GuardarOficinaInput = z.object({
    id: z.string().trim().min(1).optional(), // si falta, se crea con id autogenerado
    nombre: z.string().trim().min(1),
    activo: z.boolean().default(true),
});
/** Cancelar un préstamo pendiente (antes de que sea aceptado). */
export const CancelarPrestamoInput = z.object({
    expedienteId: z.string().min(1),
    prestamoId: z.string().min(1),
    observaciones: observacion,
});
/** Entrega de turno: traspasar TODA la custodia del actor a otro usuario. */
export const EntregaTurnoInput = z.object({
    destinatarioEmail: uid,
    observaciones: observacion,
});
/** Alta masiva de expedientes (gestión masiva). */
export const GestionMasivaInput = z.object({
    expedientes: z.array(CrearExpedienteInput).min(1).max(500),
});
/** Asignar roles a un usuario (solo admin). */
export const AsignarRolesInput = z.object({
    uid,
    roles: z.array(z.enum(['gestor', 'creador', 'admin'])),
});
// ===== Panel "Usuarios y accesos" (solo admin) =============================
const rolesArr = z.array(z.enum(['gestor', 'creador', 'admin']));
const accesosArr = z.array(z.string());
const departamentosArr = z.array(z.string());
/** Crear un usuario (con contraseña inicial opcional). */
export const CrearUsuarioInput = z.object({
    email: z.string().trim().email(),
    nombre: z.string().trim().min(1),
    apellido: z.string().trim().default(''),
    password: z.string().min(6).optional(),
    roles: rolesArr.default(['gestor']),
    accesos: accesosArr.default([]),
    departamentos: departamentosArr.default([]),
    grupo: z.string().nullable().default(null),
    oficinaId: z.string().min(1).nullable().default(null),
});
/** Actualizar datos/accesos de un usuario existente. */
export const ActualizarUsuarioInput = z.object({
    uid,
    nombre: z.string().trim().min(1).optional(),
    apellido: z.string().trim().optional(),
    email: z.string().trim().email().optional(),
    roles: rolesArr.optional(),
    accesos: accesosArr.optional(),
    departamentos: departamentosArr.optional(),
    grupo: z.string().nullable().optional(),
    oficinaId: z.string().min(1).nullable().optional(),
    activo: z.boolean().optional(),
});
/** Cambiar la contraseña de un usuario. */
export const CambiarPasswordInput = z.object({
    uid,
    password: z.string().min(6, 'Mínimo 6 caracteres'),
});
/** Eliminar un usuario. */
export const EliminarUsuarioInput = z.object({ uid });
/** Carga masiva de usuarios. */
export const CargaMasivaUsuariosInput = z.object({
    usuarios: z
        .array(z.object({ email: z.string().trim().email(), nombre: z.string().trim().default(''), departamento: z.string().trim().optional() }))
        .min(1)
        .max(500),
    rolesDefecto: rolesArr.default(['gestor']),
    accesosDefecto: accesosArr.default([]),
});
//# sourceMappingURL=esquemas.js.map